<template>
  <div class="migration-notice" @click.self="close" @keydown="onKeydown">
    <div
      ref="dialog"
      class="migration-notice__card"
      role="dialog"
      aria-modal="true"
      aria-labelledby="migration-notice-title"
      aria-describedby="migration-notice-summary"
    >
      <button
        ref="close"
        type="button"
        class="migration-notice__close"
        aria-label="Close migration notice"
        @click="close"
      >
        <feather-icon icon="XIcon" svgClasses="w-5 h-5" />
      </button>

      <section class="migration-notice__main">
        <p class="migration-notice__eyebrow">
          <feather-icon icon="CheckIcon" svgClasses="w-4 h-4" />
          Migration complete
        </p>
        <h2 id="migration-notice-title" class="migration-notice__title">
          Your clinic has moved to the new Fresh platform
        </h2>
        <p id="migration-notice-summary" class="migration-notice__summary">
          Continue to the new platform for your day-to-day clinic work.
        </p>

        <ul class="migration-notice__points">
          <li>
            <span class="migration-notice__point-icon"><feather-icon icon="ZapIcon" svgClasses="w-4 h-4" /></span>
            <span>
              <strong>Everything you know, now improved</strong>
              The same Fresh functionality in a faster, simpler experience.
            </span>
          </li>
          <li>
            <span class="migration-notice__point-icon"><feather-icon icon="UsersIcon" svgClasses="w-4 h-4" /></span>
            <span>
              <strong>Your records moved with you</strong>
              Your patients and appointment records are ready in the new platform.
            </span>
          </li>
          <li>
            <span class="migration-notice__point-icon"><feather-icon icon="ShoppingBagIcon" svgClasses="w-4 h-4" /></span>
            <span>
              <strong>Access the Fresh Store</strong>
              Order clinic products directly from the new platform.
            </span>
          </li>
        </ul>

        <div class="migration-notice__history">
          <feather-icon icon="FileTextIcon" svgClasses="w-5 h-5" class="migration-notice__history-icon" />
          <div>
            <strong>Historical Drugbook records were not moved</strong>
            <p>Your previous Drugbook records and balances were not moved to the new platform.</p>
          </div>
        </div>
      </section>

      <section class="migration-notice__aside">
        <p class="migration-notice__label">Your new web address</p>
        <h3 class="migration-notice__aside-title">Continue on the new Fresh platform</h3>
        <p class="migration-notice__url">{{ v2Url }}</p>

        <div class="migration-notice__login">
          <feather-icon icon="LockIcon" svgClasses="w-5 h-5" />
          <span>
            <strong>Your login details haven't changed</strong>
            Sign in with the email and password you already use. No reset is needed.
          </span>
        </div>

        <a :href="v2Url" class="migration-notice__cta">
          Go to the new Fresh platform
          <feather-icon icon="ArrowRightIcon" svgClasses="w-5 h-5" />
        </a>
        <p class="migration-notice__bookmark">
          <feather-icon icon="BookmarkIcon" svgClasses="w-3 h-3" />
          Bookmark the new address for next time.
        </p>
      </section>
    </div>
  </div>
</template>

<script>
const FOCUSABLE = 'a[href], button:not([disabled])';

export default {
  name: 'MigrationNoticeModal',
  props: {
    v2Url: { type: String, required: true },
  },
  // Render on <body> so layout transforms and overflow cannot clip the overlay.
  mounted() {
    document.body.appendChild(this.$el);
    this.$refs.close.focus();
  },
  beforeDestroy() {
    if (this.$el.parentNode) this.$el.parentNode.removeChild(this.$el);
  },
  methods: {
    close() {
      this.$emit('close');
    },
    // Esc closes; Tab stays inside the dialog while it is open.
    onKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = this.$refs.dialog.querySelectorAll(FOCUSABLE);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
  },
};
</script>

<style scoped>
.migration-notice {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 53000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(14, 67, 51, 0.55);
  overflow-y: auto;
}
.migration-notice,
.migration-notice * {
  box-sizing: border-box;
}
.migration-notice__card {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr);
  width: 100%;
  max-width: 1040px;
  max-height: calc(100vh - 32px);
  overflow-y: auto;
  background: #fff;
  border-radius: 20px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.25);
  color: #0e4333;
}
.migration-notice__close {
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 50%;
  background: #fff;
  color: #0e4333;
  cursor: pointer;
}
.migration-notice__main {
  padding: 56px 48px 40px;
}
.migration-notice__aside {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 56px 48px 40px;
  background: #eef6f1;
}
.migration-notice__eyebrow {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 12px;
  font-weight: 600;
}
.migration-notice__title {
  margin-bottom: 16px;
  font-size: 2.5rem;
  font-weight: 700;
  line-height: 1.1;
  color: #0e4333;
}
.migration-notice__summary {
  margin-bottom: 24px;
  font-size: 1.1rem;
  color: #5b6b65;
}
.migration-notice__points {
  margin: 0 0 24px;
  padding: 0;
  list-style: none;
}
.migration-notice__points li {
  display: flex;
  gap: 16px;
  margin-bottom: 18px;
  color: #5b6b65;
}
.migration-notice__points strong,
.migration-notice__history strong,
.migration-notice__login strong {
  display: block;
  color: #0e4333;
}
.migration-notice__point-icon {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #eef6f1;
  color: #0e4333;
}
.migration-notice__history {
  display: flex;
  gap: 16px;
  padding-top: 20px;
  border-top: 1px solid #e3ebe7;
  color: #5b6b65;
  font-size: 0.9rem;
}
.migration-notice__history-icon {
  flex-shrink: 0;
  color: #0e4333;
}
.migration-notice__history p {
  margin: 4px 0 0;
}
.migration-notice__label {
  margin-bottom: 8px;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.migration-notice__aside-title {
  margin-bottom: 16px;
  font-size: 2rem;
  font-weight: 700;
  line-height: 1.15;
  color: #0e4333;
}
.migration-notice__url {
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #d3e3da;
  font-size: 1.1rem;
  font-weight: 600;
  word-break: break-all;
}
.migration-notice__login {
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  color: #5b6b65;
  font-size: 0.9rem;
}
.migration-notice__cta {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 16px 24px;
  border-radius: 10px;
  background: #0e4333;
  box-shadow: 0 8px 20px rgba(14, 67, 51, 0.25);
  color: #fff;
  font-size: 1.05rem;
  font-weight: 600;
}
.migration-notice__cta:hover,
.migration-notice__cta:focus {
  background: #145c46;
  color: #fff;
}
.migration-notice__close:focus-visible,
.migration-notice__cta:focus-visible {
  outline: 3px solid #84cdb8;
  outline-offset: 2px;
}
.migration-notice__bookmark {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 12px;
  font-size: 0.8rem;
  color: #5b6b65;
}
@media (max-width: 768px) {
  .migration-notice__card {
    grid-template-columns: minmax(0, 1fr);
  }
  .migration-notice__main,
  .migration-notice__aside {
    padding: 48px 20px 28px;
  }
  .migration-notice__aside {
    padding-top: 28px;
  }
  .migration-notice__title {
    font-size: 1.9rem;
  }
  .migration-notice__aside-title {
    font-size: 1.5rem;
  }
}
</style>
